import './CryptoPage.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, TrendingDown, Flame, Search, Filter, 
  ChevronDown, Check, RefreshCw, Compass, Newspaper, Globe,
  Activity, ArrowUpRight, ArrowDownRight, Layers, ExternalLink
} from 'lucide-react';
import { useUserPreferences } from '../context/UserPreferencesContext';
import Sparkline, { generateSparklineData } from '../components/Sparkline';

// Crypto Indices Bar Component
const CryptoIndicesBar = ({ topCryptos, loading }) => {
  const displayCryptos = topCryptos.slice(0, 5);
  
  return (
    <div className="indices-bar">
      {loading ? (
        <div className="indices-loading">
          <RefreshCw size={14} className="spinning" />
          <span>Loading prices...</span>
        </div>
      ) : (
        displayCryptos.map((crypto) => (
          <div key={crypto.id} className="index-card">
            <div className="index-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                {crypto.image && (
                  <img 
                    src={crypto.image} 
                    alt={crypto.symbol} 
                    style={{ width: 18, height: 18, borderRadius: '50%' }}
                  />
                )}
                <span className="index-symbol">{crypto.symbol?.toUpperCase()}</span>
              </div>
              {crypto.price_change_percentage_24h >= 0 ? (
                <ArrowUpRight size={14} className="positive" />
              ) : (
                <ArrowDownRight size={14} className="negative" />
              )}
            </div>
            <div className="index-price">
              {crypto.current_price >= 1 
                ? crypto.current_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : crypto.current_price?.toFixed(4)
              }
            </div>
            <div className={`index-change ${crypto.price_change_percentage_24h >= 0 ? 'positive' : 'negative'}`}>
              {crypto.price_change_percentage_24h >= 0 ? '+' : ''}{crypto.price_change_24h?.toFixed(2)} ({crypto.price_change_percentage_24h >= 0 ? '+' : ''}{crypto.price_change_percentage_24h?.toFixed(2)}%)
            </div>
          </div>
        ))
      )}
    </div>
  );
};

function CryptoPage({ onOpenModal }) {
  const { t, formatPrice } = useUserPreferences();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('trending');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterRef = useRef(null);

  // Data state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cryptoData, setCryptoData] = useState([]);
  const [cryptoNews, setCryptoNews] = useState([]);
  const [defiNews, setDefiNews] = useState([]);

  // Categorize crypto
  const getCryptoCategory = (id) => {
    const defi = ['uniswap', 'aave', 'chainlink', 'maker', 'lido-dao', 'rocket-pool', 'compound-governance-token'];
    const meme = ['dogecoin', 'shiba-inu', 'pepe', 'floki', 'bonk', 'dogwifhat'];
    const layer2 = ['matic-network', 'arbitrum', 'optimism', 'immutable-x'];
    
    if (defi.includes(id)) return 'defi';
    if (meme.includes(id)) return 'meme';
    if (layer2.includes(id)) return 'layer2';
    return 'layer1';
  };

  // Fetch crypto data from CoinGecko
  const fetchCryptoData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await fetch(
        'https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=50&page=1&sparkline=true&price_change_percentage=24h'
      );
      
      if (!response.ok) throw new Error('API Error');
      
      const data = await response.json();
      
      const processedData = data.map(crypto => ({
        ...crypto,
        category: getCryptoCategory(crypto.id),
        sparklineData: crypto.sparkline_in_7d?.price?.slice(-24) || generateSparklineData(crypto.price_change_percentage_24h || 0)
      }));
      
      setCryptoData(processedData);
    } catch (error) {
      console.error('Failed to fetch crypto data:', error);
      setCryptoData(getMockData());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Mock data fallback
  const getMockData = () => [
    { id: 'bitcoin', symbol: 'btc', name: 'Bitcoin', image: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png', current_price: 101250, price_change_24h: 4520.50, price_change_percentage_24h: 4.67, total_volume: 42500000000, market_cap: 1980000000000, category: 'layer1', sparklineData: generateSparklineData(4.67) },
    { id: 'ethereum', symbol: 'eth', name: 'Ethereum', image: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png', current_price: 3845.20, price_change_24h: 156.80, price_change_percentage_24h: 4.25, total_volume: 18200000000, market_cap: 462000000000, category: 'layer1', sparklineData: generateSparklineData(4.25) },
    { id: 'tether', symbol: 'usdt', name: 'Tether', image: 'https://assets.coingecko.com/coins/images/325/small/Tether.png', current_price: 1.00, price_change_24h: 0.001, price_change_percentage_24h: 0.01, total_volume: 95000000000, market_cap: 120000000000, category: 'layer1', sparklineData: generateSparklineData(0.01) },
    { id: 'binancecoin', symbol: 'bnb', name: 'BNB', image: 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png', current_price: 715.40, price_change_24h: 28.50, price_change_percentage_24h: 4.15, total_volume: 2100000000, market_cap: 104000000000, category: 'layer1', sparklineData: generateSparklineData(4.15) },
    { id: 'solana', symbol: 'sol', name: 'Solana', image: 'https://assets.coingecko.com/coins/images/4128/small/solana.png', current_price: 224.50, price_change_24h: 18.45, price_change_percentage_24h: 8.95, total_volume: 8400000000, market_cap: 103000000000, category: 'layer1', sparklineData: generateSparklineData(8.95) },
    { id: 'ripple', symbol: 'xrp', name: 'XRP', image: 'https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png', current_price: 2.34, price_change_24h: 0.28, price_change_percentage_24h: 13.59, total_volume: 12800000000, market_cap: 134000000000, category: 'layer1', sparklineData: generateSparklineData(13.59) },
    { id: 'dogecoin', symbol: 'doge', name: 'Dogecoin', image: 'https://assets.coingecko.com/coins/images/5/small/dogecoin.png', current_price: 0.412, price_change_24h: 0.045, price_change_percentage_24h: 12.26, total_volume: 6200000000, market_cap: 60000000000, category: 'meme', sparklineData: generateSparklineData(12.26) },
    { id: 'cardano', symbol: 'ada', name: 'Cardano', image: 'https://assets.coingecko.com/coins/images/975/small/cardano.png', current_price: 1.08, price_change_24h: 0.09, price_change_percentage_24h: 9.09, total_volume: 2800000000, market_cap: 38000000000, category: 'layer1', sparklineData: generateSparklineData(9.09) },
    { id: 'avalanche-2', symbol: 'avax', name: 'Avalanche', image: 'https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png', current_price: 48.75, price_change_24h: 4.25, price_change_percentage_24h: 9.55, total_volume: 1900000000, market_cap: 19000000000, category: 'layer1', sparklineData: generateSparklineData(9.55) },
    { id: 'chainlink', symbol: 'link', name: 'Chainlink', image: 'https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png', current_price: 24.80, price_change_24h: 2.15, price_change_percentage_24h: 9.49, total_volume: 1400000000, market_cap: 15000000000, category: 'defi', sparklineData: generateSparklineData(9.49) },
    { id: 'polkadot', symbol: 'dot', name: 'Polkadot', image: 'https://assets.coingecko.com/coins/images/12171/small/polkadot.png', current_price: 9.25, price_change_24h: 0.68, price_change_percentage_24h: 7.94, total_volume: 720000000, market_cap: 12000000000, category: 'layer1', sparklineData: generateSparklineData(7.94) },
    { id: 'matic-network', symbol: 'matic', name: 'Polygon', image: 'https://assets.coingecko.com/coins/images/4713/small/polygon.png', current_price: 0.62, price_change_24h: 0.05, price_change_percentage_24h: 8.77, total_volume: 580000000, market_cap: 6000000000, category: 'layer2', sparklineData: generateSparklineData(8.77) },
    { id: 'uniswap', symbol: 'uni', name: 'Uniswap', image: 'https://assets.coingecko.com/coins/images/12504/small/uniswap.png', current_price: 14.20, price_change_24h: 1.12, price_change_percentage_24h: 8.55, total_volume: 420000000, market_cap: 8500000000, category: 'defi', sparklineData: generateSparklineData(8.55) },
    { id: 'shiba-inu', symbol: 'shib', name: 'Shiba Inu', image: 'https://assets.coingecko.com/coins/images/11939/small/shiba.png', current_price: 0.0000285, price_change_24h: 0.0000032, price_change_percentage_24h: 12.63, total_volume: 1200000000, market_cap: 16000000000, category: 'meme', sparklineData: generateSparklineData(12.63) },
    { id: 'litecoin', symbol: 'ltc', name: 'Litecoin', image: 'https://assets.coingecko.com/coins/images/2/small/litecoin.png', current_price: 115.40, price_change_24h: 8.25, price_change_percentage_24h: 7.70, total_volume: 890000000, market_cap: 8600000000, category: 'layer1', sparklineData: generateSparklineData(7.70) },
  ];

  // Fetch news
  const fetchNews = useCallback(async () => {
    const mockCryptoNews = [
      { id: 1, title: 'Bitcoin Surges Past $100K as Institutional Demand Grows', source: 'CoinDesk', time: '1h ago', ticker: 'BTC', breaking: true, sentiment: 'bullish' },
      { id: 2, title: 'Ethereum ETF Inflows Hit Record $1.2B in Single Day', source: 'The Block', time: '2h ago', ticker: 'ETH', breaking: true, sentiment: 'bullish' },
      { id: 3, title: 'Solana DEX Volume Exceeds Ethereum for Third Week', source: 'Decrypt', time: '3h ago', ticker: 'SOL', breaking: false, sentiment: 'bullish' },
      { id: 4, title: 'XRP Lawsuit Settlement Talks Progress', source: 'Bloomberg', time: '4h ago', ticker: 'XRP', breaking: false, sentiment: 'neutral' },
      { id: 5, title: 'Dogecoin Rallies on X Payments Integration Rumors', source: 'CryptoSlate', time: '5h ago', ticker: 'DOGE', breaking: false, sentiment: 'bullish' },
      { id: 6, title: 'Cardano Hard Fork Scheduled for Q1 2025', source: 'U.Today', time: '6h ago', ticker: 'ADA', breaking: false, sentiment: 'neutral' },
    ];

    const mockDefiNews = [
      { id: 1, title: 'Aave V4 Launch Brings Cross-Chain Liquidity', source: 'DeFi Pulse', time: '1h ago', breaking: true, sentiment: 'bullish' },
      { id: 2, title: 'Uniswap Hits $2T Cumulative Volume Milestone', source: 'The Block', time: '2h ago', breaking: true, sentiment: 'bullish' },
      { id: 3, title: 'Lido Staking Dominance Raises Decentralization Concerns', source: 'CoinDesk', time: '3h ago', breaking: false, sentiment: 'bearish' },
      { id: 4, title: 'MakerDAO Considers Treasury Diversification', source: 'Decrypt', time: '4h ago', breaking: false, sentiment: 'neutral' },
      { id: 5, title: 'Curve Finance TVL Rebounds After Exploit Recovery', source: 'DeFi Llama', time: '5h ago', breaking: false, sentiment: 'bullish' },
      { id: 6, title: 'New Restaking Protocol EigenLayer Gains Traction', source: 'Blockworks', time: '6h ago', breaking: false, sentiment: 'neutral' },
    ];

    setCryptoNews(mockCryptoNews);
    setDefiNews(mockDefiNews);
  }, []);

  // Format helpers
  const formatCryptoPrice = (price) => {
    if (!price) return '--';
    if (price >= 1000) return formatPrice(price);
    if (price >= 1) return formatPrice(price);
    if (price >= 0.0001) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(8)}`;
  };

  const formatVolume = (vol) => {
    if (!vol) return '--';
    if (vol >= 1e12) return `${(vol / 1e12).toFixed(1)}T`;
    if (vol >= 1e9) return `${(vol / 1e9).toFixed(1)}B`;
    if (vol >= 1e6) return `${(vol / 1e6).toFixed(1)}M`;
    if (vol >= 1e3) return `${(vol / 1e3).toFixed(1)}K`;
    return vol.toString();
  };

  // Effects
  useEffect(() => {
    fetchCryptoData();
    fetchNews();
  }, [fetchCryptoData, fetchNews]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options
  const filterOptions = [
    { id: 'all', label: 'All Crypto' },
    { id: 'layer1', label: 'Layer 1' },
    { id: 'layer2', label: 'Layer 2' },
    { id: 'defi', label: 'DeFi' },
    { id: 'meme', label: 'Meme Coins' },
  ];

  const tabs = [
    { id: 'trending', label: 'Trending', icon: <Flame size={14} /> },
    { id: 'gainers', label: 'Top Gainers', icon: <TrendingUp size={14} /> },
    { id: 'losers', label: 'Top Losers', icon: <TrendingDown size={14} /> },
    { id: 'defi', label: 'DeFi', icon: <Layers size={14} /> },
    { id: 'discover', label: 'Discover', icon: <Compass size={14} /> },
  ];

  // Get filtered data based on tab
  const getFilteredData = () => {
    let data = [...cryptoData];
    
    switch (activeTab) {
      case 'gainers':
        data = data.filter(c => c.price_change_percentage_24h > 0)
                   .sort((a, b) => b.price_change_percentage_24h - a.price_change_percentage_24h);
        break;
      case 'losers':
        data = data.filter(c => c.price_change_percentage_24h < 0)
                   .sort((a, b) => a.price_change_percentage_24h - b.price_change_percentage_24h);
        break;
      case 'defi':
        data = data.filter(c => c.category === 'defi');
        break;
      case 'discover':
        data = data.slice(15);
        break;
      default:
        break;
    }
    
    if (selectedFilter !== 'all') {
      data = data.filter(c => c.category === selectedFilter);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      data = data.filter(c => 
        c.symbol?.toLowerCase().includes(query) ||
        c.name?.toLowerCase().includes(query)
      );
    }
    
    return data;
  };

  // Navigate to crypto research page (row click)
  const handleCryptoClick = (symbol) => {
    navigate(`/stock/${symbol.toUpperCase()}`);
  };

  // Navigate to fullscreen chart (chart button)
  const handleViewChart = (symbol, e) => {
    e.stopPropagation();
    const chartSymbol = symbol.toUpperCase().includes('-USD') ? symbol.toUpperCase() : `${symbol.toUpperCase()}-USD`;
    navigate(`/chart/${chartSymbol}`);
  };
  
  const handleFilterSelect = (filterId) => {
    setSelectedFilter(filterId);
    setShowFilterDropdown(false);
  };
  
  const handleRefresh = () => {
    fetchCryptoData(true);
  };

  const filteredCryptos = getFilteredData();
  const currentFilterLabel = filterOptions.find(f => f.id === selectedFilter)?.label || 'All Crypto';

  const getSentimentBadge = (sentiment) => {
    switch (sentiment) {
      case 'bullish':
        return <span className="sentiment-badge bullish"><TrendingUp size={10} /> {t('bullish')}</span>;
      case 'bearish':
        return <span className="sentiment-badge bearish"><TrendingDown size={10} /> {t('bearish')}</span>;
      default:
        return <span className="sentiment-badge neutral"><Activity size={10} /> {t('neutral')}</span>;
    }
  };

  return (
    <div className="crypto-page">
      {/* Header with Market Status */}
      <div className="page-header">
        <div className="page-title-section">
          <div className="title-row">
            <h1>{t('crypto')}</h1>
            <div className="market-status" style={{ '--status-color': '#10b981' }}>
              <span className="status-dot"></span>
              <span className="status-label">24/7</span>
              <span className="status-sublabel">{t('live')}</span>
            </div>
          </div>
          <p>Real-time cryptocurrency prices from CoinGecko</p>
        </div>
        <div className="header-actions">
          <button 
            className={`refresh-btn ${refreshing ? 'spinning' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Crypto Indices Bar (Top 5 cryptos) */}
      <CryptoIndicesBar topCryptos={cryptoData} loading={loading} />

      {/* Controls */}
      <div className="controls-row">
        <div className="tab-buttons">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="search-filter-group">
          <div className="search-box">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search crypto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Type Filter */}
          <div className="filter-dropdown" ref={filterRef}>
            <button className="filter-btn" onClick={() => setShowFilterDropdown(!showFilterDropdown)}>
              <Filter size={14} />
              {currentFilterLabel}
              <ChevronDown size={14} />
            </button>
            
            {showFilterDropdown && (
              <div className="filter-menu">
                {filterOptions.map(option => (
                  <button
                    key={option.id}
                    className={`filter-option ${selectedFilter === option.id ? 'selected' : ''}`}
                    onClick={() => handleFilterSelect(option.id)}
                  >
                    {option.label}
                    {selectedFilter === option.id && <Check size={12} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="content-grid">
        {/* Data Table */}
        <div className="table-container">
          {loading ? (
            <div className="loading-state">
              <RefreshCw size={20} className="spinning" />
              <p>Loading crypto data...</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>SYMBOL</th>
                  <th>NAME</th>
                  <th>CHART</th>
                  <th>{t('price').toUpperCase()}</th>
                  <th>{t('change').toUpperCase()}</th>
                  <th>{t('volume').toUpperCase()}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredCryptos.map((crypto) => (
                  <tr key={crypto.id} className="table-row" onClick={() => handleCryptoClick(crypto.symbol)}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {crypto.image && (
                          <img 
                            src={crypto.image} 
                            alt={crypto.symbol} 
                            style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0 }}
                          />
                        )}
                        <span className="symbol">{crypto.symbol?.toUpperCase()}</span>
                        {crypto.category === 'meme' && <span className="cap-badge small">M</span>}
                        {crypto.category === 'defi' && <span className="cap-badge mid">D</span>}
                      </div>
                    </td>
                    <td><span className="name">{crypto.name}</span></td>
                    <td>
                      <Sparkline 
                        data={crypto.sparklineData} 
                        color={crypto.price_change_percentage_24h >= 0 ? '#10b981' : '#ef4444'}
                        width={60}
                        height={24}
                      />
                    </td>
                    <td>{formatCryptoPrice(crypto.current_price)}</td>
                    <td className={crypto.price_change_percentage_24h >= 0 ? 'positive' : 'negative'}>
                      <span className="change-value">
                        {crypto.price_change_percentage_24h >= 0 ? '+' : ''}{crypto.price_change_24h?.toFixed(2)}
                      </span>
                      <span className="change-percent">
                        ({crypto.price_change_percentage_24h >= 0 ? '+' : ''}{crypto.price_change_percentage_24h?.toFixed(2)}%)
                      </span>
                    </td>
                    <td className="volume">{formatVolume(crypto.total_volume)}</td>
                    <td>
                      <button className="chart-btn" onClick={(e) => handleViewChart(crypto.symbol, e)} title="Open Full Chart">
                        <ExternalLink size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* News Sidebar */}
        <div className="news-sidebar">
          {/* Crypto News */}
          <div className="news-panel">
            <div className="news-header">
              <Newspaper size={14} />
              <span>Crypto News</span>
              <span className="live-badge">LIVE</span>
            </div>
            <div className="news-list">
              {cryptoNews.map(item => (
                <div key={item.id} className={`news-item ${item.breaking ? 'breaking' : ''}`} onClick={() => handleCryptoClick(item.ticker)}>
                  {item.breaking && <span className="breaking-dot" />}
                  <div className="news-content">
                    <p className="news-title">{item.title}</p>
                    <div className="news-meta">
                      <span className="ticker">${item.ticker}</span>
                      {getSentimentBadge(item.sentiment)}
                      <span className="news-time">{item.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* DeFi News */}
          <div className="news-panel">
            <div className="news-header">
              <Globe size={14} />
              <span>DeFi News</span>
              <span className="live-badge">LIVE</span>
            </div>
            <div className="news-list">
              {defiNews.map(item => (
                <div key={item.id} className={`news-item ${item.breaking ? 'breaking' : ''}`}>
                  {item.breaking && <span className="breaking-dot" />}
                  <div className="news-content">
                    <p className="news-title">{item.title}</p>
                    <div className="news-meta">
                      {getSentimentBadge(item.sentiment)}
                      <span className="news-source">{item.source}</span>
                      <span className="news-time">{item.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CryptoPage;